// src/components/FeedbackWidget.js
import React, { useEffect, useRef, useState } from 'react';
import apiService from '../services/api';
import { useAuth } from '../contexts/CognitoAuthContext';
import { APP_VERSION, DEFAULT_CLIENT_ID } from '../config';

/**
 * In-app user feedback (plan §10).
 *
 * A single, persistent floating "Feedback" button (bottom-right) mounted once
 * app-wide. Opens a small modal where an authenticated user files a Bug,
 * Feature request, or Question with type-specific fields. Context (route,
 * userAgent, viewport, app version) is auto-captured client-side.
 *
 * IMPORTANT: tenant + user are NOT trusted from this client — the server stamps
 * them from the deploy config + the validated Cognito session. The DEFAULT_CLIENT_ID
 * shown here is for display only.
 */

const TYPES = [
  { id: 'bug', label: 'Bug', hint: 'Something is broken or wrong' },
  { id: 'feature', label: 'Feature request', hint: 'Something you wish existed' },
  { id: 'question', label: 'Question', hint: 'Ask us anything' },
];

const SEVERITIES = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
  { id: 'critical', label: 'Critical' },
];

const IMPORTANCES = [
  { id: 'nice-to-have', label: 'Nice to have' },
  { id: 'important', label: 'Important' },
  { id: 'critical', label: 'Critical' },
];

const PURPLE = 'var(--pallium-primary-purple)';

const emptyForm = {
  type: 'bug',
  description: '',
  stepsToReproduce: '',
  severity: 'medium',
  problemItSolves: '',
  importance: 'important',
};

const FeedbackWidget = () => {
  const { isAuthenticated, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Lightweight ring buffer of recent console errors, attached to bug reports
  // to help triage. Reversible: we restore the original console.error on unmount.
  const consoleErrorsRef = useRef([]);
  useEffect(() => {
    const original = console.error;
    console.error = (...args) => {
      try {
        const line = args
          .map((a) => (a instanceof Error ? a.message : String(a)))
          .join(' ');
        consoleErrorsRef.current.push(line);
        if (consoleErrorsRef.current.length > 15) {
          consoleErrorsRef.current.shift();
        }
      } catch (_e) {
        // never let logging capture break the app
      }
      original.apply(console, args);
    };
    return () => {
      console.error = original;
    };
  }, []);

  // Only render for authenticated users (never on the login/reset screens).
  if (!isAuthenticated) return null;

  const resetAndClose = () => {
    setIsOpen(false);
    // Defer reset so the closing transition doesn't show a flash of empty form.
    setTimeout(() => {
      setForm(emptyForm);
      setSubmitted(false);
      setError(null);
      setIsSubmitting(false);
    }, 200);
  };

  const captureContext = () => {
    const context = {
      route:
        typeof window !== 'undefined'
          ? window.location.pathname + window.location.search
          : undefined,
      appVersion: APP_VERSION,
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      viewport:
        typeof window !== 'undefined'
          ? `${window.innerWidth}x${window.innerHeight}`
          : undefined,
      occurredAt: new Date().toISOString(),
    };
    if (form.type === 'bug' && consoleErrorsRef.current.length > 0) {
      context.consoleErrors = consoleErrorsRef.current.join('\n');
    }
    return context;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) {
      setError('Please add a short description.');
      return;
    }
    setIsSubmitting(true);
    setError(null);

    const payload = {
      type: form.type,
      description: form.description.trim(),
      context: captureContext(),
    };
    if (form.type === 'bug') {
      payload.stepsToReproduce = form.stepsToReproduce.trim() || undefined;
      payload.severity = form.severity;
    } else if (form.type === 'feature') {
      payload.problemItSolves = form.problemItSolves.trim() || undefined;
      payload.importance = form.importance;
    }

    try {
      await apiService.submitFeedback(payload);
      setSubmitted(true);
    } catch (err) {
      console.error('Feedback submission failed:', err);
      setError('Could not send feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const labelStyle = {
    display: 'block',
    fontSize: '0.8125rem',
    fontWeight: 600,
    marginBottom: '0.25rem',
    color: 'var(--pallium-text-primary)',
  };
  const inputStyle = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    borderRadius: '0.5rem',
    border: '1px solid var(--pallium-stroke-light)',
    fontSize: '0.875rem',
    backgroundColor: 'var(--pallium-white)',
    color: 'var(--pallium-text-primary)',
  };

  return (
    <>
      {/* Persistent floating entry point */}
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label="Submit feedback"
          title="Submit feedback"
          style={{
            position: 'fixed',
            right: '1.25rem',
            bottom: '1.25rem',
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            minHeight: '44px',
            minWidth: '44px',
            padding: '0.625rem 1rem',
            borderRadius: '9999px',
            border: 'none',
            cursor: 'pointer',
            backgroundColor: PURPLE,
            color: 'var(--pallium-white)',
            fontSize: '0.875rem',
            fontWeight: 600,
            boxShadow: 'var(--pallium-shadow-lg)',
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M18 5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h2v3l4-3h6a2 2 0 002-2V5z" />
          </svg>
          Feedback
        </button>
      )}

      {/* Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) resetAndClose();
          }}
        >
          <div
            className="rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            style={{
              backgroundColor: 'var(--pallium-white)',
              boxShadow: 'var(--pallium-shadow-lg)',
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Submit feedback"
          >
            <div className="flex items-center justify-between mb-4">
              <h2
                className="text-xl font-bold"
                style={{ color: 'var(--pallium-text-primary)' }}
              >
                {submitted ? 'Thank you!' : 'Send feedback'}
              </h2>
              <button
                type="button"
                onClick={resetAndClose}
                aria-label="Close"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  lineHeight: 1,
                  color: 'var(--pallium-text-secondary)',
                }}
              >
                ×
              </button>
            </div>

            {submitted ? (
              <div>
                <div
                  className="mb-4 p-3 rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--pallium-success-green-bg, #f0fdf4)',
                    border: '1px solid var(--pallium-success-green, #22c55e)',
                    color: 'var(--pallium-success-green, #166534)',
                  }}
                >
                  Your feedback was sent to the Pallium team. Thank you for
                  helping us improve.
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={resetAndClose}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      border: 'none',
                      cursor: 'pointer',
                      backgroundColor: PURPLE,
                      color: 'var(--pallium-white)',
                      fontWeight: 600,
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Type selector */}
                <div className="mb-4">
                  <span style={labelStyle}>What kind of feedback?</span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {TYPES.map((t) => {
                      const active = form.type === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() =>
                            setForm((prev) => ({ ...prev, type: t.id }))
                          }
                          title={t.hint}
                          style={{
                            flex: 1,
                            padding: '0.5rem',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            border: `1px solid ${
                              active ? PURPLE : 'var(--pallium-stroke-light)'
                            }`,
                            backgroundColor: active
                              ? 'var(--pallium-selected-option-bg, #efeafc)'
                              : 'var(--pallium-white)',
                            color: active
                              ? PURPLE
                              : 'var(--pallium-text-secondary)',
                          }}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {error && (
                  <div
                    className="mb-3 p-3 rounded-lg text-sm"
                    style={{
                      backgroundColor: 'var(--pallium-alert-error-bg, #fef2f2)',
                      border: '1px solid var(--pallium-error-red, #ef4444)',
                      color: 'var(--pallium-error-red, #991b1b)',
                    }}
                  >
                    {error}
                  </div>
                )}

                {/* Description (all types) */}
                <div className="mb-4">
                  <label htmlFor="feedback-description" style={labelStyle}>
                    {form.type === 'question'
                      ? 'Your question'
                      : 'Description'}
                    <span style={{ color: 'var(--pallium-error-red)' }}> *</span>
                  </label>
                  <textarea
                    id="feedback-description"
                    value={form.description}
                    onChange={update('description')}
                    rows={form.type === 'question' ? 5 : 3}
                    placeholder={
                      form.type === 'bug'
                        ? 'What went wrong?'
                        : form.type === 'feature'
                        ? 'What would you like to see?'
                        : 'How can we help?'
                    }
                    style={{ ...inputStyle, resize: 'vertical' }}
                    required
                  />
                </div>

                {/* Bug-specific fields */}
                {form.type === 'bug' && (
                  <>
                    <div className="mb-4">
                      <label htmlFor="feedback-steps" style={labelStyle}>
                        Steps to reproduce
                      </label>
                      <textarea
                        id="feedback-steps"
                        value={form.stepsToReproduce}
                        onChange={update('stepsToReproduce')}
                        rows={3}
                        placeholder="1. Go to… 2. Click… 3. See…"
                        style={{ ...inputStyle, resize: 'vertical' }}
                      />
                    </div>
                    <div className="mb-4">
                      <label htmlFor="feedback-severity" style={labelStyle}>
                        Severity
                      </label>
                      <select
                        id="feedback-severity"
                        value={form.severity}
                        onChange={update('severity')}
                        style={inputStyle}
                      >
                        {SEVERITIES.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {/* Feature-specific fields */}
                {form.type === 'feature' && (
                  <>
                    <div className="mb-4">
                      <label htmlFor="feedback-problem" style={labelStyle}>
                        What problem would this solve?
                      </label>
                      <textarea
                        id="feedback-problem"
                        value={form.problemItSolves}
                        onChange={update('problemItSolves')}
                        rows={2}
                        placeholder="The job you're trying to get done"
                        style={{ ...inputStyle, resize: 'vertical' }}
                      />
                    </div>
                    <div className="mb-4">
                      <label htmlFor="feedback-importance" style={labelStyle}>
                        How important is this to you?
                      </label>
                      <select
                        id="feedback-importance"
                        value={form.importance}
                        onChange={update('importance')}
                        style={inputStyle}
                      >
                        {IMPORTANCES.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between mt-2">
                  <span
                    style={{
                      fontSize: '0.6875rem',
                      color: 'var(--pallium-text-secondary)',
                    }}
                    title="Auto-attached for triage"
                  >
                    {DEFAULT_CLIENT_ID} · v{APP_VERSION}
                    {user?.email ? ` · ${user.email}` : ''}
                  </span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={resetAndClose}
                      style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '0.5rem',
                        border: '1px solid var(--pallium-stroke-light)',
                        cursor: 'pointer',
                        backgroundColor: 'var(--pallium-white)',
                        color: 'var(--pallium-text-secondary)',
                        fontWeight: 600,
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '0.5rem',
                        border: 'none',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        backgroundColor: PURPLE,
                        color: 'var(--pallium-white)',
                        fontWeight: 600,
                        opacity: isSubmitting ? 0.7 : 1,
                      }}
                    >
                      {isSubmitting ? 'Sending…' : 'Send feedback'}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
